import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Grid3x3, Users, ArrowRight, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Icon } from "@/lib/icon";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import SeoFooter from "@/components/public/SeoFooter";
import RelatedServices from "@/components/public/RelatedServices";
import { Picture } from "@/components/public/Picture";
import { useLead } from "@/components/public/LeadDialog";
import { useVirtualTour, TourOverlay } from "@/components/public/VirtualTour";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  useSeo("/");
  const [data, setData] = useState(null);
  const { openLead } = useLead();

  useEffect(() => {
    api.get("/public/homepage").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { settings, hero, quick_actions, stats, workspace_types, features, locations, media, brands } = data;
  const flags = settings.feature_flags || {};
  const sec = hero.sections || {};

  return (
    <div className="bg-white">
      <Header phone={settings.phone} />
      <Hero hero={hero} openLead={openLead} />
      <SearchAndActions quick_actions={quick_actions} openLead={openLead} />
      {flags.stats !== false && <Stats stats={stats} />}
      {flags.workspaces !== false && <Workspaces items={workspace_types} h={sec.workspaces} />}
      <Features features={features} tagline={settings.tagline} openLead={openLead} h={sec.features} />
      {flags.locations !== false && <Locations locations={locations} city={settings.city} openLead={openLead} h={sec.locations} />}
      {flags.virtual_walkthrough !== false && <VirtualWalkthrough h={sec.virtual} />}
      {flags.media !== false && <Media media={media} h={sec.media} />}
      {flags.brands !== false && <Brands brands={brands} />}
      <RelatedServices />
      <SeoFooter />
      <Footer settings={settings} />
    </div>
  );
}

function Hero({ hero, openLead }) {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-sky-50/70 to-white">
      <div className="container-cz grid lg:grid-cols-2 gap-10 items-center pt-14 pb-24 lg:pt-20">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="eyebrow mb-4">{hero.eyebrow}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.98] text-slate-900">
            {hero.title_line1}<br />
            <span className="text-primary">{hero.title_line2}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-md">{hero.subtitle}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button data-testid="hero-primary-cta" onClick={() => navigate(hero.primary_cta?.href || "/locations")} className="rounded-full h-12 px-7 gap-2 text-base shadow-cz">
              {hero.primary_cta?.label || "Find a Workspace"} <ArrowRight className="w-4 h-4" />
            </Button>
            <Button data-testid="hero-secondary-cta" onClick={() => openLead("enterprise")} variant="outline" className="rounded-full h-12 px-7 text-base border-slate-300">
              {hero.secondary_cta?.label || "Talk to Our Team"}
            </Button>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="relative">
          <div className="relative rounded-[28px] overflow-hidden shadow-cz aspect-[4/3.2]">
            <Picture src={hero.image} alt={hero.image_alt} loading="eager" width={1000} className="w-full h-full object-cover" />
            <div className="absolute top-6 right-6 bg-white/95 backdrop-blur px-4 py-3 rounded-2xl shadow-lg">
              <p className="font-display font-extrabold text-slate-900 leading-tight text-sm tracking-tight">{hero.badge_text}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SearchAndActions({ quick_actions, openLead }) {
  const navigate = useNavigate();
  const [opts, setOpts] = useState({ locations: [], workspace_types: [], team_sizes: [] });
  const [loc, setLoc] = useState("");
  const [type, setType] = useState("");
  const [size, setSize] = useState("");

  useEffect(() => {
    api.get("/public/search-options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  const doSearch = () => {
    navigate(loc ? `/locations/${loc}` : "/locations");
  };

  return (
    <section className="relative -mt-16 z-10">
      <div className="container-cz">
        <motion.div {...fade} className="card-cz shadow-cz p-4 md:p-5">
          <div className="grid sm:grid-cols-3 gap-3">
            {quick_actions.map((q) => (
              <button key={q.id} data-testid={`quick-${q.icon}`}
                onClick={() => q.title.includes("Tour") && q.title.includes("Virtual") ? openLead("general") : openLead(q.title.includes("Quote") ? "quote" : "tour")}
                className="group flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 hover:border-primary/40 hover:bg-sky-50/50 transition-all text-left">
                <span className="w-11 h-11 rounded-lg bg-sky-100 text-primary flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <Icon name={q.icon} className="w-5 h-5" />
                </span>
                <span>
                  <span className="block font-semibold text-slate-900 text-sm">{q.title}</span>
                  <span className="block text-xs text-slate-500">{q.subtitle}</span>
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SearchField({ icon: I, label, children }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-slate-200 bg-white">
      <I className="w-5 h-5 text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="block text-[11px] text-slate-400 font-medium">{label}</span>
        {children}
      </div>
    </div>
  );
}

function Stats({ stats }) {
  return (
    <section className="py-14">
      <div className="container-cz grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <motion.div key={s.id} {...fade} transition={{ ...fade.transition, delay: i * 0.08 }}
            className="flex items-center gap-4" data-testid={`stat-${i}`}>
            <span className="w-12 h-12 rounded-xl bg-sky-100 text-primary flex items-center justify-center shrink-0">
              <Icon name={s.icon} className="w-6 h-6" />
            </span>
            <div>
              <div className="font-display text-3xl font-extrabold text-slate-900">{s.value}</div>
              <div className="text-sm text-slate-500">{s.label}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Workspaces({ items, h = {} }) {
  const navigate = useNavigate();
  return (
    <section className="py-16 bg-slate-50">
      <div className="container-cz">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-3">{h.eyebrow || "Our Workspaces"}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 max-w-2xl">{h.title || "Flexible Space Solutions Tailored to Suit You"}</h2>
          </div>
          <button onClick={() => navigate("/workspaces")} data-testid="view-all-workspaces" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all shrink-0">
            View All Workspaces <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {items.map((w, i) => (
            <motion.button key={w.id} {...fade} transition={{ ...fade.transition, delay: i * 0.05 }}
              onClick={() => navigate(`/workspaces/${w.slug}`)} data-testid={`workspace-card-${w.slug}`}
              className="group card-cz overflow-hidden text-left hover:shadow-cz transition-all duration-300 hover:-translate-y-1">
              <div className="aspect-[4/3] overflow-hidden">
                <Picture src={w.image} alt={w.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-slate-900 leading-snug">{w.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5 line-clamp-2">{w.description}</p>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 text-primary mt-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features({ features, tagline, openLead, h = {} }) {
  return (
    <section className="py-16">
      <div className="container-cz grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fade}>
          <p className="eyebrow mb-3">{h.eyebrow || "Why CoworkZen"}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{h.title || "We have all what you need"}</h2>
          <p className="text-slate-600 mt-4 max-w-md">{h.subtitle || "From modern infrastructure to vibrant communities, we provide everything to make your workday better."}</p>
          <Button onClick={() => openLead("enterprise")} data-testid="features-know-more" className="rounded-full h-12 px-7 mt-7 gap-2">
            Know More <ArrowRight className="w-4 h-4" />
          </Button>
        </motion.div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <motion.div key={f.id} {...fade} transition={{ ...fade.transition, delay: i * 0.05 }}
              className="card-cz p-5 flex flex-col items-center text-center gap-3 hover:shadow-cz hover:-translate-y-1 transition-all" data-testid={`feature-${i}`}>
              <span className="w-12 h-12 rounded-full bg-sky-100 text-primary flex items-center justify-center">
                <Icon name={f.icon} className="w-6 h-6" />
              </span>
              <span className="text-sm font-semibold text-slate-800 leading-tight">{f.title}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Locations({ locations, city, openLead, h = {} }) {
  const navigate = useNavigate();
  return (
    <section className="py-16 bg-slate-50">
      <div className="container-cz">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-3">{h.eyebrow || "Our Locations"}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{h.title || `Prime Business Locations in ${city || "Noida"}`}</h2>
          </div>
          <button onClick={() => navigate("/locations")} data-testid="view-all-locations" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all shrink-0">
            View All Locations <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {locations.map((l, i) => (
            <motion.div key={l.id} {...fade} transition={{ ...fade.transition, delay: i * 0.08 }}
              className="group card-cz overflow-hidden hover:shadow-cz transition-all duration-300" data-testid={`location-card-${l.slug}`}>
              <div className="aspect-[16/10] overflow-hidden">
                <Picture src={l.image} alt={`${l.name}, ${l.sector}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-bold text-slate-900">{l.name}</h3>
                <p className="text-sm text-primary font-medium mt-0.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {l.sector}</p>
                <p className="text-sm text-slate-500 mt-2.5 line-clamp-2">{l.description}</p>
                <div className="flex gap-2.5 mt-5">
                  <Button onClick={() => navigate(`/locations/${l.slug}`)} data-testid={`view-space-${l.slug}`} className="rounded-full h-10 px-4 gap-1.5 flex-1 text-sm">
                    View Space <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                  <Button onClick={() => openLead("tour", { location: l.slug, city: (l.sector || "").split(",").pop().trim() })} data-testid={`book-tour-${l.slug}`} variant="outline" className="rounded-full h-10 px-4 flex-1 text-sm border-slate-300">
                    Book a Tour
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VirtualWalkthrough({ h = {} }) {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const vt = useVirtualTour();

  useEffect(() => {
    api.get("/public/virtual-walkthroughs").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  if (!items.length) return null;
  const cur = items[Math.min(active, items.length - 1)];

  return (
    <section className="py-16 bg-slate-50" data-testid="home-virtual-walkthrough">
      <div className="container-cz">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="eyebrow mb-3">{h.eyebrow || "Virtual Walkthrough"}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{h.title || "Step Inside Before You Visit"}</h2>
          <p className="text-slate-600 mt-4">{h.subtitle || "Take an immersive 360° tour of our centres from anywhere. Pick a location and explore every corner in real detail."}</p>
        </div>
        <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
          <motion.div {...fade} className="relative rounded-3xl overflow-hidden shadow-cz aspect-[16/9] bg-slate-200">
            <Picture src={cur.preview_image} alt={`${cur.name} 360° walkthrough preview`} className="w-full h-full object-cover" />
            <TourOverlay onClick={() => vt.openTour(cur)} testid={`vw-play-${cur.location_slug}`} />
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-4 py-2 rounded-2xl shadow-lg">
              <p className="font-display font-bold text-slate-900 leading-tight">{cur.name}</p>
              {cur.sector && <p className="text-xs text-primary flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {cur.sector}</p>}
            </div>
          </motion.div>
          <div className="space-y-2.5">
            <p className="text-sm font-semibold text-slate-500 px-1">Select a location</p>
            {items.map((it, i) => (
              <button key={it.id} onClick={() => setActive(i)} data-testid={`vw-select-${it.location_slug}`}
                className={`w-full flex items-center gap-3 p-2.5 rounded-2xl border text-left transition-all ${i === active ? "border-primary bg-white shadow-cz" : "border-slate-200 bg-white/60 hover:border-slate-300"}`}>
                <span className="w-16 h-12 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                  <Picture src={it.preview_image} alt={it.name} loading="lazy" width={160} className="w-full h-full object-cover" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-slate-900 text-sm truncate">{it.name}</span>
                  <span className="block text-xs text-slate-500 truncate">{it.sector}</span>
                </span>
                <ArrowRight className={`w-4 h-4 shrink-0 transition-colors ${i === active ? "text-primary" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Media({ media, h = {} }) {
  return (
    <section className="py-16">
      <div className="container-cz">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow mb-3">{h.eyebrow || "In the Media"}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{h.title || "CoworkZen in the News"}</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {media.map((m, i) => (
            <motion.a key={m.id} {...fade} transition={{ ...fade.transition, delay: i * 0.06 }}
              href={m.link} target="_blank" rel="noopener noreferrer" data-testid={`media-${m.id}`}
              className="group card-cz p-5 flex flex-col hover:shadow-cz hover:-translate-y-1 transition-all">
              <img src={m.logo} alt={m.publication} loading="lazy" className="h-7 w-auto object-contain object-left mb-4" onError={(e) => { e.target.style.display = "none"; }} />
              <p className="text-xs font-semibold text-primary">{m.publication}</p>
              <p className="text-sm font-medium text-slate-800 mt-1.5 line-clamp-3 flex-1">{m.title}</p>
              <span className="inline-flex items-center gap-1 text-xs text-slate-400 mt-3 group-hover:text-primary transition-colors">
                Read article <ChevronRight className="w-3.5 h-3.5" />
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Brands({ brands }) {
  const loop = [...brands, ...brands];
  return (
    <section className="py-12 border-t border-slate-100">
      <div className="container-cz">
        <p className="eyebrow text-center mb-8">Trusted by Leading Businesses</p>
        <div className="relative overflow-hidden no-scrollbar">
          <div className="flex items-center gap-14 animate-marquee w-max">
            {loop.map((b, i) => (
              <a key={`${b.id}-${i}`} href={b.website} target="_blank" rel="noopener noreferrer" className="shrink-0 grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all" data-testid={`brand-${b.id}-${i}`}>
                <img src={b.logo} alt={b.name} loading="lazy" className="h-8 w-auto object-contain" onError={(e) => { e.target.replaceWith(Object.assign(document.createElement("span"), { className: "font-display font-bold text-slate-400 text-lg", innerText: b.name })); }} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
